import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { colors } from '@guryou/colors';
import { stateIsLoaded, stateIsLoading, useUserVideoCourse, useVideoCourse } from '@guryou/redux';
import { SkeletonLoader, Pagination, Icon } from '../../components/shared';
import { useWidgetAuthLockedProvider } from '../../hooks/useWidgetAuthLockedProvider';
import PublicVideoCoursesLanding from './PublicVideoCoursesLanding';
import VideoCourseCard from './VideoCourseCard';

const VideoCoursesPanel = () => {
  const { t } = useTranslation();
  const videoCourses = useVideoCourse();
  const userVideoCourses = useUserVideoCourse();
  const lockProviderId = useWidgetAuthLockedProvider();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('all');
  const userVideoCoursesData = userVideoCourses?.data || [];

  const vcourses = (stateIsLoaded(videoCourses) && videoCourses?.data?.length && videoCourses.data) || [];

  // Show only widget provider courses when logged in through auth widget.
  const allVideoCourses = lockProviderId ? vcourses.filter(course => String(course?.providerId) === lockProviderId) : vcourses || [];
  const filteredVideoCourses = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) {
      return allVideoCourses;
    }
    return allVideoCourses.filter(course => {
      const name = String(course?.name || '').toLowerCase();
      const providerName = String(course?.providerName || '').toLowerCase();
      const description = String(course?.description || '')
        .replace(/<[^>]*>/g, ' ')
        .toLowerCase();
      return name.includes(q) || providerName.includes(q) || description.includes(q);
    });
  }, [allVideoCourses, searchTerm]);

  const enrolledCourseIds = useMemo(() => {
    const ids = new Set();
    userVideoCoursesData.forEach(item => {
      if (item?.id != null) {
        ids.add(String(item.id));
      }
      if (item?.tempProductId != null) {
        ids.add(String(item.tempProductId));
      }
    });
    return ids;
  }, [userVideoCoursesData]);

  const filteredByTab = useMemo(() => {
    if (tab === 'mine') {
      return filteredVideoCourses.filter(course => enrolledCourseIds.has(String(course?.id)) || enrolledCourseIds.has(String(course?.tempProductId)));
    }
    return filteredVideoCourses;
  }, [filteredVideoCourses, tab, enrolledCourseIds]);

  const totalItems = filteredByTab.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Get current page items
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVideoCourses = filteredByTab.slice(startIndex, endIndex);

  useEffect(() => {
    if (stateIsLoaded(videoCourses)) {
      setIsInitialLoad(false);
    }
  }, [videoCourses]);

  useEffect(() => {
    setCurrentPage(1);
  }, [lockProviderId, searchTerm, tab]);

  // Pagination functions
  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  // Show loading only during initial load
  if (isInitialLoad && (!stateIsLoaded(videoCourses) || !videoCourses?.data)) {
    return (
      <section className="pt-0 pb-16 lg:pb-24" style={{ backgroundColor: colors.white }}>
        <div className="w-full px-0">
          <div className="bg-white px-4 sm:px-6 lg:px-8 pt-6 mb-8">
            <div className="mb-4 pb-4 border-b border-gray-200" style={{ flexShrink: 0 }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100">
                  <div className="w-6 h-6 rounded animate-pulse bg-gray-200"></div>
                </div>
                <div>
                  <div className="h-8 bg-gray-300 rounded mb-2 w-64 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-44 animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="w-full h-48 bg-gray-300 animate-pulse"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-300 rounded mb-4 animate-pulse"></div>
                    <div className="h-4 bg-gray-300 rounded mb-2 animate-pulse"></div>
                    <div className="h-4 bg-gray-300 rounded mb-2 animate-pulse"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4 animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-0 pb-16 lg:pb-24" style={{ backgroundColor: colors.white }}>
      <div className="w-full px-0">
        {/* Header equal to calendar structure */}
        <div className="bg-white px-4 sm:px-6 lg:px-8 pt-6 mb-8">
          <div className="mb-4 pb-4 border-b border-gray-200" style={{ flexShrink: 0 }}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-lg"
                  style={{
                    backgroundColor: 'rgba(var(--feel-primary-rgb), 0.18)',
                  }}
                >
                  <svg className="w-6 h-6 text-feel-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-5.197-3.03A1 1 0 008 9v6a1 1 0 001.555.832l5.197-3.03a1 1 0 000-1.664z"
                    />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('VIDEO_COURSE_PAGE.videoCourses')}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {totalItems} {totalItems === 1 ? t('GENERAL.event', 'evento') : t('GENERAL.events', 'eventi')}
                  </p>
                </div>
              </div>
              <div className="w-full sm:w-auto sm:min-w-[560px] flex items-center gap-2">
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => setTab('all')}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      tab === 'all' ? 'text-white bg-[var(--feel-primary)]' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t('GENERAL.all', 'Tutti')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab('mine')}
                    className={`px-3 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                      tab === 'mine' ? 'text-white bg-[var(--feel-primary)]' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t('VIDEO_COURSE_PAGE.myVideos', 'I miei video')}
                  </button>
                </div>
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder={t('GENERAL.search', 'Cerca')}
                    className="w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-feel-primary focus:border-feel-primary"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video Courses Grid */}
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {stateIsLoading(videoCourses) ? (
              // Skeleton Loader for Video Courses
              <SkeletonLoader type="compactCard" count={6} />
            ) : (
              paginatedVideoCourses?.map((course, index) => (
                <VideoCourseCard
                  course={course}
                  key={index}
                  isEnrolled={enrolledCourseIds.has(String(course?.id)) || enrolledCourseIds.has(String(course?.tempProductId))}
                />
              ))
            )}
          </div>

          {/* No Video Courses Message */}
          {!stateIsLoading(videoCourses) && (!paginatedVideoCourses || paginatedVideoCourses.length === 0) && (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center p-4 mb-6 rounded-full" style={{ backgroundColor: colors.carouselPink }}>
                <Icon name="play" size="lg" className="text-mandy" />
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: colors.mineShaft }}>
                {t('VIDEO_COURSE_PAGE.noVideoCourses')}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">{t('VIDEO_COURSE_PAGE.noVideoCoursesDescription')}</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              startIndex={startIndex}
              endIndex={endIndex}
              totalItems={totalItems}
            />
          )}
        </div>
      </div>
    </section>
  );
};

const VideoCourses = () => {
  const auth = useSelector(state => state.auth);
  const lockProviderId = useWidgetAuthLockedProvider();
  const isLoggedIn = stateIsLoaded(auth) && !!auth?.data;
  const usePanelLayout = isLoggedIn || !!lockProviderId;

  if (!usePanelLayout) {
    return <PublicVideoCoursesLanding />;
  }

  return <VideoCoursesPanel />;
};

export default VideoCourses;
